import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth-store'

type Mode = 'password' | 'magic-link'

export default function LoginPage() {
  const [mode, setMode] = useState<Mode>('password')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [magicLinkSent, setMagicLinkSent] = useState(false)
  const navigate = useNavigate()
  const setSession = useAuthStore((s) => s.setSession)

  async function handlePasswordLogin(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError(null)

    const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password })

    setLoading(false)

    if (authError) {
      setError(authError.message)
      return
    }

    setSession(data.session)
    navigate('/')
  }

  async function handleMagicLink(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError(null)

    const { error: authError } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: false,
        emailRedirectTo: window.location.origin,
      },
    })

    setLoading(false)

    if (authError) {
      setError(authError.message)
      return
    }

    setMagicLinkSent(true)
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="w-full max-w-sm space-y-6 rounded-xl border border-border bg-card p-8 shadow-sm">
        <div className="space-y-1 text-center">
          <h1 className="text-2xl font-bold tracking-tight">OneIdol Dashboard</h1>
          <p className="text-sm text-muted-foreground">Masuk ke akun manajemen Anda</p>
        </div>

        {/* Mode toggle */}
        <div className="flex rounded-lg border border-border bg-muted/40 p-1 text-sm">
          <button
            type="button"
            onClick={() => { setMode('magic-link'); setError(null); setMagicLinkSent(false) }}
            className={`flex-1 rounded-md py-1.5 font-medium transition-colors ${
              mode === 'magic-link'
                ? 'bg-background shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Link Login
          </button>
          <button
            type="button"
            onClick={() => { setMode('password'); setError(null) }}
            className={`flex-1 rounded-md py-1.5 font-medium transition-colors ${
              mode === 'password'
                ? 'bg-background shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Password
          </button>
        </div>

        {/* Magic link mode */}
        {mode === 'magic-link' && (
          magicLinkSent ? (
            <div className="rounded-lg border border-border bg-muted/30 p-4 text-center space-y-1">
              <p className="text-sm font-medium">Link login dikirim!</p>
              <p className="text-xs text-muted-foreground">
                Cek email <span className="font-medium">{email}</span> dan klik link yang dikirim untuk masuk.
              </p>
              <button
                type="button"
                onClick={() => { setMagicLinkSent(false); setEmail('') }}
                className="mt-2 text-xs text-primary hover:underline"
              >
                Kirim ke email lain
              </button>
            </div>
          ) : (
            <form onSubmit={handleMagicLink} className="space-y-4">
              <div className="space-y-1">
                <label htmlFor="email-otp" className="text-sm font-medium">
                  Email
                </label>
                <input
                  id="email-otp"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="kamu@example.com"
                />
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {loading ? 'Mengirim...' : 'Kirim Link Login'}
              </button>

              <p className="text-center text-xs text-muted-foreground">
                Kami kirim link ke email Anda. Klik link tersebut untuk masuk, tanpa perlu password.
              </p>
            </form>
          )
        )}

        {/* Password mode */}
        {mode === 'password' && (
          <form onSubmit={handlePasswordLogin} className="space-y-4">
            <div className="space-y-1">
              <label htmlFor="email" className="text-sm font-medium">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="kamu@example.com"
              />
            </div>

            <div className="space-y-1">
              <label htmlFor="password" className="text-sm font-medium">
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? 'Memuat...' : 'Masuk'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
